<!--
  EmbroiderySizeConflictNotice -- the heads-up under a size in Add placement
  (Jake, 2026-11-02).

  One embroidery stitch file cannot serve two sizes more than the tolerance
  (10%) apart, so an artwork placed at such a size is held at design publish
  until that size has its own artwork: new digitization and approval. This
  says so BEFORE the distributor saves, and names the size(s) the artwork is
  already used at (in live stores).

  Presentational only: the SERVER decides (GET /api/artworks/<id>/size-conflicts,
  `size_conflict`) and sends the tolerance, so the wizard never carries a
  number of its own. A status note with no buttons: nothing is blocked, the
  host's submit stays enabled.
-->
<template>
    <!--
        The live region is ALWAYS in the DOM (empty until a conflict exists):
        a region inserted already filled is often not announced. Only the
        content inside it comes and goes.
    -->
    <div
        role="status"
        aria-live="polite"
        data-testid="embroidery-size-conflict-region"
    >
        <div
            v-if="sizes.length"
            class="sizeconflict"
            data-testid="embroidery-size-conflict"
        >
            <span
                class="material-icons sizeconflict__icon"
                aria-hidden="true"
            >info</span>
            <p class="sizeconflict__text">
                <strong>This size needs new digitization and approval.</strong>
                This artwork is already used at {{ sizeList }}. A size more than
                {{ conflict.tolerance_pct }}% different needs its own artwork,
                because one stitch file can't be sewn at both sizes. Expect an
                added digitization step (cost and time) and approval before orders
                at this size can be produced. You can still add this placement.
            </p>
        </div>
    </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
    // The server's `size_conflict`: {method, tolerance_pct, existing_sizes},
    // or null/undefined when there is nothing to say (fail open).
    conflict: { type: Object, default: null },
});

const sizes = computed(() => {
    const list = props.conflict?.existing_sizes;
    return Array.isArray(list) ? list : [];
});

// 4 -> "4", 4.5 -> "4.5": no trailing zeros.
const fmt = (n) => String(Number(Number(n).toFixed(2)));

const sizeList = computed(() => {
    const labels = sizes.value.map((s) => `${fmt(s.width_in)} x ${fmt(s.height_in)} in`);
    if (labels.length <= 1) return labels[0] || "";
    return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
});
</script>

<style scoped>
.sizeconflict {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 6px 0 0;
    padding: 6px 8px;
    font-size: 12px;
    line-height: 1.4;
    border: 1px solid var(--tf-warning, #fcb900);
    border-radius: 4px;
    background: var(--tf-warning-soft, #fff8e1);
    color: var(--tf-warning-dark, #8a6d3b);
}
.sizeconflict__icon {
    font-size: 16px;
    flex: none;
}
.sizeconflict__text {
    margin: 0;
}
</style>
